(function (root) {
  'use strict';

  var SESSION_START_KEY = 'ev:klaviyo-session-started-at:v1';
  var SESSION_ENGAGED_KEY = 'ev:klaviyo-session-engaged:v1';
  var SESSION_OPENED_KEY = 'ev:klaviyo-session-opened:v1';
  var SESSION_CLAIMED_KEY = 'ev:klaviyo-discount-claimed:v1';
  var SUPPRESSED_UNTIL_KEY = 'ev:klaviyo-form-suppressed-until:v1';
  var SUBMITTED_KEY = 'ev:klaviyo-form-submitted:v1';
  var PURCHASE_STARTED_KEY = 'ev:klaviyo-purchase-started:v1';
  var PURCHASE_ACTIVE_CLASS = 'klaviyo-purchase-active';
  var OFFER_PAUSED_CLASS = 'klaviyo-offer-paused';
  var MANUAL_LAUNCHER_ACTIVE_CLASS = 'klaviyo-manual-launcher-active';
  var THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000;

  function createSessionTrigger(options) {
    var now = options.now;
    var openForm = options.openForm;
    var schedule = options.schedule;
    var storage = options.storage;
    var thresholdMs = options.thresholdMs || 30000;

    function getStartedAt() {
      var storedStart = Number(storage.getItem(SESSION_START_KEY));
      if (Number.isFinite(storedStart) && storedStart > 0) {
        return storedStart;
      }

      var startedAt = now();
      storage.setItem(SESSION_START_KEY, startedAt);
      return startedAt;
    }

    function maybeOpen() {
      if (storage.getItem(SESSION_OPENED_KEY) === 'true') return;
      if (storage.getItem(SESSION_ENGAGED_KEY) !== 'true') return;
      if (now() - getStartedAt() < thresholdMs) return;

      storage.setItem(SESSION_OPENED_KEY, 'true');
      openForm();
    }

    function start() {
      var startedAt = getStartedAt();
      var remainingMs = Math.max(0, thresholdMs - (now() - startedAt));
      schedule(maybeOpen, remainingMs);
    }

    function engage() {
      storage.setItem(SESSION_ENGAGED_KEY, 'true');
      maybeOpen();
    }

    return { engage: engage, start: start };
  }

  function installBrowserTrigger(options) {
    var doc = options.document;
    var formId = options.formId;
    var now = options.now || Date.now;
    var pageMode = options.pageMode || 'auto';
    var win = options.window;
    var storage = options.storage;
    var couponSuccess = options.couponSuccess || win.EVKlaviyoCouponSuccessController;

    if (!storage) {
      try {
        storage = win.sessionStorage;
      } catch (error) {
        var fallbackValues = {};
        storage = {
          getItem: function (key) {
            return Object.prototype.hasOwnProperty.call(fallbackValues, key)
              ? fallbackValues[key]
              : null;
          },
          setItem: function (key, value) {
            fallbackValues[key] = String(value);
          },
        };
      }
    }

    var persistentStorage = options.persistentStorage || storage;
    var cooldownMs = options.cooldownMs || THREE_DAYS_MS;

    var launcher = doc.querySelector
      ? doc.querySelector('[data-klaviyo-teaser-launcher]')
      : null;
    var cartDrawer = doc.querySelector ? doc.querySelector('cart-drawer') : null;
    var checkoutInProgress = false;
    var offersPaused = storage.getItem(PURCHASE_STARTED_KEY) === 'true';
    var manualOpen = false;
    var formOpen = false;
    var cartStateKnown = true;
    var cartWasOpen = false;

    function cartIsOpen() {
      return cartDrawer && cartDrawer.getAttribute('data-open') === 'true';
    }

    function discountIsActive() {
      return cartStateKnown && cartDrawer && !!cartDrawer.querySelector('[data-cart-discount][data-discount-active="true"]');
    }

    function purchaseHasStarted() {
      var path = win.location ? win.location.pathname : '';
      return checkoutInProgress || cartIsOpen()
        || /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(?:cart|checkout|checkouts|orders)(?:\/|$)/i.test(path);
    }

    function suppressPurchaseOffers() {
      offersPaused = true;
      manualOpen = false;
      formOpen = false;
      storage.setItem(PURCHASE_STARTED_KEY, 'true');
      if (doc.documentElement) {
        doc.documentElement.classList.add(OFFER_PAUSED_CLASS);
        doc.documentElement.classList.toggle(PURCHASE_ACTIVE_CLASS, !!purchaseHasStarted());
      }
      setLauncherVisible(false);
      if (couponSuccess && couponSuccess.close) couponSuccess.close();
      releasePageInteraction();
    }

    function getPopupDialog() {
      if (!doc.querySelector) return null;

      var popup = doc.querySelector('[data-testid="POPUP"]');
      return popup && popup.closest ? popup.closest('[role="dialog"]') : null;
    }

    function releasePageInteraction() {
      var dialog = getPopupDialog();
      if (dialog && dialog.style) {
        dialog.style.setProperty('pointer-events', 'none', 'important');
      }

      if (doc.body && doc.body.classList) {
        doc.body.classList.remove('klaviyo-prevent-body-scrolling');
      }
    }

    function restorePopupInteraction() {
      var dialog = getPopupDialog();
      if (dialog && dialog.style) {
        dialog.style.removeProperty('pointer-events');
      }
    }

    function setLauncherVisible(visible) {
      visible = visible && !purchaseHasStarted() && cartStateKnown && !formOpen;
      if (launcher) {
        var claimed = isClaimedInSession();
        var active = !!discountIsActive();
        var state = active ? 'active' : (claimed ? 'ready' : 'offer');
        var label = launcher.querySelector
          ? launcher.querySelector('[data-klaviyo-teaser-label]')
          : null;
        launcher.hidden = !visible;
        launcher.setAttribute('data-claimed', String(claimed));
        launcher.setAttribute('data-discount-active', String(active));
        // Copy is rendered by Liquid in the current storefront language.
        if (launcher.getAttribute) {
          launcher.setAttribute('aria-label', launcher.getAttribute('data-aria-' + state) || '');
          if (label) label.textContent = launcher.getAttribute('data-label-' + state) || '';
        }
        var value = launcher.querySelector ? launcher.querySelector('[data-klaviyo-teaser-value]') : null;
        if (value) value.textContent = active ? '✓' : '10 %';
      }

      var rootClassList = doc.documentElement && doc.documentElement.classList;
      if (!rootClassList) return;

      if (visible) {
        rootClassList.add(MANUAL_LAUNCHER_ACTIVE_CLASS);
      } else {
        rootClassList.remove(MANUAL_LAUNCHER_ACTIVE_CLASS);
      }
    }

    function openForm(preferDirectOpen) {
      if (purchaseHasStarted()) return;
      if (discountIsActive()) return;
      if (offersPaused && preferDirectOpen !== true) return;
      if (isSuppressed() && !isClaimedInSession()) return;
      manualOpen = preferDirectOpen === true;
      if (doc.documentElement) doc.documentElement.classList.remove(OFFER_PAUSED_CLASS);
      if (isClaimedInSession()) {
        if (couponSuccess && couponSuccess.openSaved()) {
          formOpen = true;
          setLauncherVisible(false);
        }
        return;
      }
      formOpen = true;
      setLauncherVisible(false);
      storage.setItem(SESSION_OPENED_KEY, 'true');
      restorePopupInteraction();
      if (
        preferDirectOpen === true
        && win.klaviyo
        && typeof win.klaviyo.openForm === 'function'
      ) {
        win.klaviyo.openForm(formId);
        return;
      }
      win._klOnsite = win._klOnsite || [];
      if (
        preferDirectOpen === true
        && typeof win._klOnsite.openForm === 'function'
      ) {
        win._klOnsite.openForm(formId);
        return;
      }
      win._klOnsite.push('openForm', formId);
    }

    function isSuppressed() {
      if (persistentStorage.getItem(SUBMITTED_KEY) === 'true') return true;

      var suppressedUntil = Number(
        persistentStorage.getItem(SUPPRESSED_UNTIL_KEY)
      );
      return Number.isFinite(suppressedUntil) && suppressedUntil > now();
    }

    function isClaimedInSession() {
      return storage.getItem(SESSION_CLAIMED_KEY) === 'true';
    }

    function showLauncher() {
      if (!launcher) return;
      var browsing = pageMode === 'manual' || pageMode === 'auto';
      var eligible = discountIsActive() || isClaimedInSession()
        || (!isSuppressed() && (pageMode === 'manual' || offersPaused));
      setLauncherVisible(browsing && eligible);
    }

    function syncCartState() {
      var open = !!cartIsOpen();
      if (cartWasOpen && !open) checkoutInProgress = false;
      cartWasOpen = open;
      if (purchaseHasStarted()) {
        suppressPurchaseOffers();
      } else {
        if (doc.documentElement) {
          doc.documentElement.classList.remove(PURCHASE_ACTIVE_CLASS);
          doc.documentElement.classList.toggle(OFFER_PAUSED_CLASS, offersPaused && !manualOpen);
        }
        showLauncher();
      }
    }

    function finishManualOffer() {
      formOpen = false;
      manualOpen = false;
      if (offersPaused && doc.documentElement) doc.documentElement.classList.add(OFFER_PAUSED_CLASS);
      releasePageInteraction();
      showLauncher();
    }

    var trigger = createSessionTrigger({
      now: now,
      openForm: openForm,
      schedule: options.schedule || win.setTimeout.bind(win),
      storage: storage,
    });

    function engage() {
      trigger.engage();
    }

    var autoEligible = pageMode === 'auto' && !isSuppressed() && !purchaseHasStarted() && !offersPaused && !discountIsActive();
    if (autoEligible) {
      doc.addEventListener('pointerdown', engage, { once: true, passive: true });
      doc.addEventListener('keydown', engage, { once: true });
      win.addEventListener('scroll', function () {
        if (win.scrollY > 8) engage();
      }, { passive: true });
    }

    win.addEventListener('klaviyoForms', function (event) {
      var detail = event && event.detail;
      if (!detail || detail.formId !== formId) return;
      if (purchaseHasStarted() || (offersPaused && !manualOpen)) {
        suppressPurchaseOffers();
        showLauncher();
        return;
      }

      if (detail.type === 'close') {
        if (isClaimedInSession()) {
          finishManualOffer();
          return;
        }
        persistentStorage.setItem(SUPPRESSED_UNTIL_KEY, now() + cooldownMs);
        finishManualOffer();
        return;
      }

      if (detail.type === 'open') {
        formOpen = true;
        storage.setItem(SESSION_OPENED_KEY, 'true');
        restorePopupInteraction();
        setLauncherVisible(false);
        return;
      }

      if (detail.type === 'submit') {
        formOpen = true;
        persistentStorage.setItem(SUBMITTED_KEY, 'true');
        storage.setItem(SESSION_CLAIMED_KEY, 'true');
        if (couponSuccess) couponSuccess.handleSubmit();
        setLauncherVisible(false);
        return;
      }

      if (detail.type === 'redirectedToUrl') {
        setLauncherVisible(false);
      }
    });

    win.addEventListener('ev:klaviyo-coupon-success-closed', function () {
      if (!isClaimedInSession()) return;
      finishManualOffer();
    });

    if (launcher) {
      launcher.addEventListener('click', function () {
        if (purchaseHasStarted() || !cartStateKnown) return;
        if (discountIsActive()) {
          if (cartDrawer && typeof cartDrawer.open === 'function') cartDrawer.open();
          else win.location.assign(launcher.getAttribute('data-cart-url') || '/cart');
          return;
        }
        openForm(true);
      });
    }

    // These are shopping signals, independent of analytics consent/transport.
    doc.addEventListener('ev:commerce', function (event) {
      if (event.detail && event.detail.name === 'cart_opened') syncCartState();
    });
    doc.addEventListener('submit', function (event) {
      var form = event.target;
      if (form && form.tagName === 'FORM' && event.submitter && event.submitter.name === 'checkout') {
        checkoutInProgress = true;
        suppressPurchaseOffers();
      }
    }, true);
    doc.addEventListener('click', function (event) {
      var target = event.target;
      var payment = target && target.closest && target.closest('[data-native-payment]');
      if (payment && !payment.hidden && !payment.hasAttribute('inert')) {
        checkoutInProgress = true;
        suppressPurchaseOffers();
      }
    }, true);
    win.addEventListener('pageshow', function (event) {
      checkoutInProgress = false;
      offersPaused = offersPaused || storage.getItem(PURCHASE_STARTED_KEY) === 'true';
      if ((event.persisted || offersPaused || discountIsActive()) && cartDrawer && typeof cartDrawer.refresh === 'function') {
        // CartDrawer catches network failures internally. Trust only the fresh
        // server-rendered body observed below, not resolution of its promise.
        cartStateKnown = false;
        Promise.resolve(cartDrawer.refresh()).catch(function () {});
      }
      syncCartState();
    });

    // A late provider callback can add its scroll lock after its open event.
    // Watch only that body's class attribute, without observing the full page.
    if (doc.body && win.MutationObserver) {
      var bodyObserver = new win.MutationObserver(function () {
        if ((purchaseHasStarted() || (offersPaused && !manualOpen)) && doc.body.classList.contains('klaviyo-prevent-body-scrolling')) {
          releasePageInteraction();
        }
      });
      bodyObserver.observe(doc.body, { attributes: true, attributeFilter: ['class'] });
    }

    if (cartDrawer && win.MutationObserver) {
      var cartObserver = new win.MutationObserver(function (records) {
        records.forEach(function (record) {
          if (record.type === 'childList' && (record.target === cartDrawer || record.target.matches('[data-cart-body]'))) {
            cartStateKnown = true;
          }
        });
        syncCartState();
      });
      cartObserver.observe(cartDrawer, { attributes: true, attributeFilter: ['data-open', 'data-discount-active'], childList: true, subtree: true });
    }

    syncCartState();

    if (autoEligible) {
      if (win.scrollY > 8) engage();
      trigger.start();
    }
    return trigger;
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      createSessionTrigger: createSessionTrigger,
      installBrowserTrigger: installBrowserTrigger,
    };
  }

  root.EVKlaviyoSessionTrigger = {
    createSessionTrigger: createSessionTrigger,
    installBrowserTrigger: installBrowserTrigger,
  };

  if (root.document) {
    var script = root.document.currentScript;
    var formId = script && script.getAttribute('data-form-id');
    var pageMode = script && script.getAttribute('data-page-mode');
    var persistentStorage;
    try {
      persistentStorage = root.localStorage;
    } catch (error) {
      persistentStorage = null;
    }
    if (formId) {
      installBrowserTrigger({
        document: root.document,
        formId: formId,
        pageMode: pageMode || 'disabled',
        persistentStorage: persistentStorage,
        window: root,
      });
    }
  }
})(typeof window === 'undefined' ? globalThis : window);
